import { writeCss } from '../../helpers/DOM'
import { doOnce } from '../../helpers/doOnce'
import WebModel from '../../models/WebModel'
import { getStyles } from '../../native/templates/helpers/getStyles'
import type { Slot } from '../Slot'
import { hideClass } from './CLSToggle'

interface IAdBoxConfig {
  height: string
  width: string
  verticalAlign: 'top' | 'center' | 'bottom'
  background: boolean
  additionalCss?: {
    root: object
    [selector: string]: object
  }
}

export const adBoxSelector = `mv-ad-box`

/**
 * Surrounds slot wrapper to provide a fixed decorative "canvas" for
 * ads to display.
 * @param config
 */
export const hasAdBox = (config: IAdBoxConfig) => (
  target: Constructor<Slot>
): any =>
  class extends target {
    template() {
      writeAdBoxStyles(config, this.model)
      const template = super.template()

      return adBoxTemplate(template, this.id, config, this.model)
    }

    /** Static positioning now makes this obsolete */
    displayAdUnitLabels(adType, { wrapper } = this) {
      const labels = wrapper.querySelector('.adunitlabel') as HTMLElement
      if (!labels) {
        return
      }

      labels.style.display = 'block'
    }

    get adBox(): HTMLElement | null {
      return document.querySelector(
        `.${adBoxSelector}[data-slotid='${this.id}']`
      )
    }
  }

const writeAdBoxStyles = doOnce((config: IAdBoxConfig, model: WebModel) =>
  writeCss(getAdBoxStyles(config, model))
)

const contentDots = '\\00B7\\00B7\\00B7'

const getAdBoxStyles = (
  config: IAdBoxConfig,
  { ad_box_bg_color }: WebModel
) => `
  body .adunitwrapper[style*='display: block']>.adunit:before {
    display: none!important;
  }

  .${adBoxSelector} {
    justify-content: center;
    position: relative;
    margin-bottom: 10px;
  }

  .${adBoxSelector} > div {
    z-index: 1;
  }

  @keyframes fadeOut {
    0% { opacity: 1;}
    100% { opacity: 0.01 }
  }

  .${adBoxSelector}.${hideClass}:before {
    content: "${contentDots}";
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    text-align: center;
    color: #C4C4C4;
    font-size: 62px;
    letter-spacing: 2px;
    z-index: 1;
    animation: fadeOut 1s linear 0s infinite;
    animation-direction: alternate;
  }
  .${adBoxSelector} .adunitlabel {
    width: 100%!important;
    left: 0; 
    bottom: 0;
    padding-left: 10px;
    padding-right: 10px;
  }
  .${adBoxSelector} .adunitwrapper {
    position: static;
    margin-bottom: 20px;
    margin-top: 0;
  }

  .${adBoxSelector} [data-slotid="recipe_btf"] {
    float: right;
  }
  
  .mv-ad-box adunitwrapper.mv-native-size {
    margin-bottom: 0px;
  }

`

const adBoxTemplate = (
  content: string,
  slotId: string,
  { height, width, verticalAlign, background, additionalCss }: IAdBoxConfig,
  { ad_box_bg_color }: WebModel
): string =>
  `
<div class="${adBoxSelector} ${hideClass}" data-slotid="${slotId}" style="height: ${height}; width: ${width};">
  ${content}
  <div></div>
  <style>
  .${adBoxSelector}[data-slotid="${slotId}"] {
    ${getStyles(alignmentStylesMap[verticalAlign || 'center'])}
    ${background ? `background-color: ${ad_box_bg_color};` : ''}
  }
  .${adBoxSelector} .adunitlabel {
    bottom: 0;
    padding-bottom: ${background ? '5px' : '0'};
  }
  ${getAdditionalCss(additionalCss, slotId)}
}
  </style>
</div>
`
const getAdditionalCss = (
  additionalCss: object | undefined,
  id: string
): string => {
  if (!additionalCss) {
    return ``
  }
  const cssArray = Object.keys(additionalCss || {}).map((key) => {
    const props = additionalCss[key]
    const styles = getStyles(props)
    /** Keyword root applies to the top-most element, `mv-ad-box` */
    const selector = key !== 'root' ? key : ''

    return `[data-slotid="${id}"] ${selector} { ${styles} }`
  })

  return cssArray.join('\n')
}

const alignmentStylesMap = {
  top: {
    display: 'block'
  },
  center: {
    display: 'flex',
    'align-items': 'center'
  },
  bottom: {
    display: 'flex',
    'align-items': 'flex-end'
  }
}

export const getAdBoxBySlot = (slot: Slot) =>
  document.querySelector(`.${adBoxSelector}[data-slotid="${slot.id}"]`)
